// src/services/orderManagementService.js
// Unified order management service
// Consolidates orderDeskService, orderDataService, orderSyncService, orderUpdateService
// Single source of truth for all OrderDesk interactions

import { db } from "./firebase";
import { collection, getDocs, query, where, onSnapshot } from "firebase/firestore";
import { parseDDMMYYYY, formatYYYYMMDD, getStartOfWeek, getWeekRange } from "./dateService.js";

// ============================================================================
// CONFIGURATION
// ============================================================================

const ORDERDESK_BASE_URL = "https://orderdesker-844411985132.europe-west2.run.app";

const CONFIG = {
    cacheTTL: 30 * 60 * 1000, // 30 minutes
    debounceDelay: 1000, // 1 second
    maxRetriesPerCall: 3,
    retryDelay: 1000
};

// ============================================================================
// CACHING SYSTEM
// ============================================================================

class OrderCache {
    constructor() {
        this.data = new Map(); // key -> orders
        this.timestamps = new Map(); // key -> timestamp
        this.ttl = CONFIG.cacheTTL;
    }

    set(key, data) {
        this.data.set(key, data);
        this.timestamps.set(key, Date.now());
        console.log(`[OrderCache] Cached ${data.length} orders for key: ${key}`);
    }

    get(key) {
        if (!this.isValid(key)) return null;
        return this.data.get(key);
    }

    isValid(key) {
        if (!this.data.has(key)) return false;
        const timestamp = this.timestamps.get(key);
        if (!timestamp) return false;
        const age = Date.now() - timestamp;
        return age < this.ttl;
    }

    clear(key = null) {
        if (key) {
            this.data.delete(key);
            this.timestamps.delete(key);
            console.log(`[OrderCache] Cleared cache for key: ${key}`);
        } else {
            this.data.clear();
            this.timestamps.clear();
            console.log(`[OrderCache] Cleared all cache`);
        }
    }

    getStats() {
        return {
            entries: this.data.size,
            totalOrders: Array.from(this.data.values()).reduce((sum, orders) => sum + orders.length, 0),
            oldestEntry: this.timestamps.size > 0 ? Math.min(...this.timestamps.values()) : null
        };
    }
}

// ============================================================================
// API CALL TRACKING
// ============================================================================

class APICallTracker {
    constructor() {
        this.calls = new Map(); // endpoint -> { count, lastCall, totalCalls }
        this.debounceTimers = new Map(); // requestKey -> timer
    }

    track(endpoint, requestKey = null) {
        const now = Date.now();
        const key = requestKey || endpoint;
        
        if (!this.calls.has(endpoint)) {
            this.calls.set(endpoint, { count: 0, lastCall: 0, totalCalls: 0 });
        }
        
        const tracker = this.calls.get(endpoint);
        tracker.count++;
        tracker.totalCalls++;
        tracker.lastCall = now;
        
        console.log(`[OrderDesk API] Call #${tracker.totalCalls} to ${endpoint} (${tracker.count} recent calls)`);
        
        if (tracker.count > 10) {
            console.warn(`[OrderDesk API] High frequency: ${tracker.count} calls to ${endpoint}`);
        }
    }

    debounce(requestKey, apiCallFn, delay = CONFIG.debounceDelay) {
        return new Promise((resolve, reject) => {
            // Clear existing timer
            if (this.debounceTimers.has(requestKey)) {
                clearTimeout(this.debounceTimers.get(requestKey));
            }
            
            // Set new timer
            const timer = setTimeout(async () => {
                this.debounceTimers.delete(requestKey);
                try {
                    const result = await apiCallFn();
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            }, delay);
            
            this.debounceTimers.set(requestKey, timer);
        });
    }

    getStats() {
        const stats = {
            totalCalls: 0,
            endpoints: {},
            warnings: []
        };
        
        for (const [endpoint, data] of this.calls.entries()) {
            stats.totalCalls += data.totalCalls;
            stats.endpoints[endpoint] = {
                totalCalls: data.totalCalls,
                recentCalls: data.count,
                lastCall: new Date(data.lastCall).toISOString()
            };
            
            if (data.count > 10) {
                stats.warnings.push(`High frequency: ${data.count} calls to ${endpoint}`);
            }
        }
        
        return stats;
    }

    clear() {
        this.calls.clear();
        this.debounceTimers.forEach(timer => clearTimeout(timer));
        this.debounceTimers.clear();
        console.log('[APICallTracker] Cleared all tracking data');
    }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function buildUrl(path = "/", params = {}) {
    const cleanPath = `/${String(path || "").replace(/^\/+/, "")}`;
    const url = new URL(cleanPath, `${ORDERDESK_BASE_URL}/`);
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && `${v}` !== "") {
            url.searchParams.set(k, String(v));
        }
    });
    return url.toString();
}

async function fetchJson(url, options = {}) {
    const res = await fetch(url, options);
    const text = await res.text();
    let data = null;
    
    try {
        data = JSON.parse(text);
    } catch {
        throw new Error(`Invalid JSON response: ${text.slice(0, 200)}`);
    }
    
    if (!res.ok) {
        const err = new Error(data?.error || `Request failed: ${res.status}`);
        err.detail = data;
        err.status = res.status;
        throw err;
    }
    
    return data;
}

const clean = (s) => (s == null ? undefined : String(s).trim());
const stripODIN = (v) => (v ? String(v).replace(/^ODIN-?/i, "").trim() : v);

// ============================================================================
// ORDER MANAGEMENT SERVICE
// ============================================================================

class OrderManagementService {
    constructor() {
        this.cache = new OrderCache();
        this.tracker = new APICallTracker();
    }

    // ========================================================================
    // FETCHING ORDERS
    // ========================================================================

    /**
     * Fetch orders from OrderDesk with optional caching and filtering
     * @param {object} options - Fetch options
     * @returns {Promise<Array>} Array of orders
     */
    async fetchOrders(options = {}) {
        const {
            folder = "All Orders",
            limit = 100,
            startDate = null,
            endDate = null,
            modifiedStartDate = null,
            modifiedEndDate = null,
            dateField = 'delivery', // 'delivery' | 'dispatch' - when folder is DD-MM-YYYY, filter by checkout_data.Delivery or Dispatch
            useCache = true,
            debounce = false
        } = options;

        // Generate cache key
        const cacheKey = `orders_${folder}_${limit}_${startDate}_${endDate}_${dateField}`;

        // Check cache first
        if (useCache) {
            const cached = this.cache.get(cacheKey);
            if (cached) {
                console.log(`[OrderManagement] Returning ${cached.length} cached orders`);
                return cached;
            }
        }

        // API call function
        const fetchFn = async () => {
            this.tracker.track('fetchOrders', cacheKey);

            const params = { folder, limit };
            if (startDate) params.search_start_date = startDate;
            if (endDate) params.search_end_date = endDate;
            if (modifiedStartDate) params.modified_start_date = modifiedStartDate;
            if (modifiedEndDate) params.modified_end_date = modifiedEndDate;
            if (dateField && dateField !== 'delivery') params.dateField = dateField;

            console.log(`[OrderManagement] Fetching orders:`, params);

            const url = buildUrl("/orders", params);
            const data = await fetchJson(url);

            const orders = Array.isArray(data?.values)
                ? data.values
                : Array.isArray(data?.orders)
                    ? data.orders
                    : [];

            console.log(`[OrderManagement] Fetched ${orders.length} orders`);

            // Cache the results
            if (useCache) {
                this.cache.set(cacheKey, orders);
            }

            return orders;
        };

        // Use debounce if requested
        if (debounce) {
            return this.tracker.debounce(cacheKey, fetchFn);
        }

        return fetchFn();
    }

    /**
     * Fetch orders for a specific week
     * @param {string} weekStart - Week start date
     * @param {object} options - Additional options
     * @returns {Promise<Array>} Array of orders
     */
    async fetchOrdersForWeek(weekStart, options = {}) {
        const weekRange = getWeekRange(weekStart);
        if (!weekRange.start || !weekRange.end) {
            throw new Error(`Invalid week start date: ${weekStart}`);
        }

        return this.fetchOrders({
            ...options,
            startDate: weekRange.start,
            endDate: weekRange.end
        });
    }

    /**
     * Fetch orders for multiple weeks
     * @param {Array<string>} weekStarts - Array of week start dates
     * @param {object} options - Additional options
     * @returns {Promise<Array>} Combined array of orders
     */
    async fetchOrdersForWeeks(weekStarts, options = {}) {
        const promises = weekStarts.map(weekStart => 
            this.fetchOrdersForWeek(weekStart, options)
        );
        
        const results = await Promise.all(promises);
        const allOrders = results.flat();
        
        // Deduplicate by order ID
        const uniqueOrders = this.deduplicateOrders(allOrders);
        
        console.log(`[OrderManagement] Fetched ${allOrders.length} orders across ${weekStarts.length} weeks (${uniqueOrders.length} unique)`);
        
        return uniqueOrders;
    }

    // ========================================================================
    // ORDER GROUPING AND TRANSFORMATION
    // ========================================================================

    /**
     * Group orders by order ID (combines line items)
     * @param {Array} orders - Array of orders
     * @returns {Array} Grouped orders
     */
    groupOrdersByOrderId(orders) {
        const map = new Map();
        
        for (const order of orders) {
            let orderId, salesChannel, customerId, customerCompany, customerFirstName, 
                customerLastName, customerPhone, email, shippingFirstName, shippingLastName, 
                shippingPhone, shippingPostalCode, sku, quantity, shippingMethod, 
                dispatchDate, deliveryDate, notes, giftOrders, status;

            // Handle array format
            if (Array.isArray(order)) {
                orderId = String(order[0] ?? '').trim();
                salesChannel = String(order[1] ?? '').trim();
                customerId = order[2] ?? '';
                customerCompany = order[3] ?? '';
                customerFirstName = order[4] ?? '';
                customerLastName = order[5] ?? '';
                customerPhone = order[6] ?? '';
                email = order[7] ?? '';
                shippingFirstName = order[8] ?? '';
                shippingLastName = order[9] ?? '';
                shippingPhone = order[10] ?? '';
                shippingPostalCode = order[11] ?? '';
                sku = order[12] ?? '';
                quantity = order[13] ?? '';
                shippingMethod = order[14] ?? '';
                dispatchDate = order[15] ?? '';
                deliveryDate = order[16] ?? '';
                notes = order[17] ?? '';
                giftOrders = order[18] ?? '';
                status = order[19] ?? 'Pending';
            } 
            // Handle object format
            else {
                orderId = String(order.order_number || order.source_id || order.id || '').trim();
                salesChannel = order.sales_channel || order.channel || '';
                customerId = order.customer_id || '';
                customerCompany = order.customer?.company || order.customer_company || '';
                customerFirstName = order.customer?.first_name || order.customer_first_name || '';
                customerLastName = order.customer?.last_name || order.customer_last_name || '';
                customerPhone = order.customer?.phone || order.customer_phone || '';
                email = order.customer?.email || order.email || '';
                shippingFirstName = order.shipping?.first_name || order.shipping_first_name || '';
                shippingLastName = order.shipping?.last_name || order.shipping_last_name || '';
                shippingPhone = order.shipping?.phone || order.shipping_phone || '';
                shippingPostalCode = order.shipping?.postal_code || order.shipping_postal_code || '';
                sku = order.sku || order.code || '';
                quantity = order.quantity || '';
                shippingMethod = order.shipping_method || order.shippingMethod || '';
                // Dispatch and Delivery are user-set in OrderDesk checkout_data (no longer calculated)
                dispatchDate = order.checkout_data?.Dispatch || order.dispatch_date || order.dispatchDate || '';
                deliveryDate = order.checkout_data?.Delivery || order.delivery_date || order.deliveryDate || '';
                notes = order.notes || '';
                giftOrders = order.gift_orders || order.giftOrders || '';
                status = order.status || 'Pending';
            }

            if (!orderId) continue;

            if (!map.has(orderId)) {
                map.set(orderId, {
                    orderId,
                    salesChannel,
                    customerId,
                    customerCompany,
                    customerFirstName,
                    customerLastName,
                    customerPhone,
                    email,
                    shippingFirstName,
                    shippingLastName,
                    shippingPhone,
                    shippingPostalCode,
                    items: [],
                    shippingMethod,
                    dispatchDate,
                    deliveryDate,
                    notes,
                    giftOrders,
                    status
                });
            }

            // Add item if SKU exists
            if (sku) {
                map.get(orderId).items.push({ sku, quantity });
            }
        }

        return Array.from(map.values());
    }

    /**
     * Deduplicate orders by order ID
     * @param {Array} orders - Array of orders
     * @returns {Array} Deduplicated orders
     */
    deduplicateOrders(orders) {
        const seen = new Set();
        return orders.filter(order => {
            const id = order.orderId || order.order_number || order.id;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }

    // ========================================================================
    // CACHE MANAGEMENT
    // ========================================================================

    /**
     * Clear cache for specific key or all cache
     * @param {string} key - Cache key (optional)
     */
    clearCache(key = null) {
        this.cache.clear(key);
    }

    /**
     * Get cache statistics
     * @returns {object} Cache stats
     */
    getCacheStats() {
        return this.cache.getStats();
    }

    // ========================================================================
    // API USAGE MONITORING
    // ========================================================================

    /**
     * Get API usage statistics
     * @returns {object} API usage stats
     */
    getAPIUsageStats() {
        return this.tracker.getStats();
    }

    /**
     * Clear API usage statistics
     */
    clearAPIUsageStats() {
        this.tracker.clear();
    }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

const orderManagementService = new OrderManagementService();

export default orderManagementService;

// Export the class for testing
export { OrderManagementService };

// Export utility functions
export {
    buildUrl,
    fetchJson,
    clean,
    stripODIN
};

